import { useQueryClient } from '@tanstack/react-query'
import { SortingState } from '@tanstack/react-table'
import { UserOrganizationStatus, UserStatus } from '@/constants/user'
import { useMutation, useQuery } from '@/hooks'
import { PaginatedResponse } from '@/models/api'

type UseGetUser = {
  page?: number
  pageSize?: number
  sort?: SortingState
  search?: string
  status?: string
}

export const useGetUserList = ({ page, pageSize, sort, search, status }: UseGetUser) => {
  return useQuery<PaginatedResponse<Profile>>({
    queryKey: ['users', { page, pageSize, sort, search, status }],
    url: '/users',
    axiosConfig: {
      params: { page, pageSize, search, status: status === UserOrganizationStatus.ALL ? '' : status },
    },
  })
}

export interface Profile {
  id: string
  user_id: string
  username: string
  firstname: string
  lastname: string
  status: UserStatus
  created_at: string
  updated_at: string
  role: {
    id: string
    name: string
    key: string
  }
  role_name: string
  role_id: string
  auth_id: string
  type: string
  is_dev: boolean
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()
  return useMutation<
    any,
    Error,
    {
      firstname: string
      lastname: string
      username: string
      password: string
      roleId: string
      status: UserStatus
    }
  >(
    ['create_users'],
    {
      url: `/users/create`,
      method: 'POST',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['users'] })
      },
    },
  )
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()
  return useMutation<any, Error, { id: string }>(
    ['delete_user'],
    {
      url: `/users/:id`,
      method: 'DELETE',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['users'] })
      },
    },
  )
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  return useMutation<
    any,
    Error,
    { id: string; username: string; firstname: string; lastname: string; roleId: string; status: UserStatus }
  >(
    ['update_user'],
    {
      url: `/users/:id`,
      method: 'PUT',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['users'] })
      },
    },
  )
}

export const useGetUserMe = () => {
  return useQuery<Profile>({
    queryKey: ['user_me'],
    url: '/users/me',
    axiosConfig: {},
    queryConfig: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    },
  })
}

export const useChangePassword = () => {
  return useMutation<any, Error, { id: string; newPassword: string }>(['change_password_user'], {
    url: `/users/:id/change-password`,
    method: 'POST',
  })
}

export const useGetUserById = (id: string) => {
  return useQuery<Profile>({
    queryKey: ['user_by_id', id],
    url: `/users/${id}`,
    axiosConfig: {},
  })
}
